import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { Loader2, UserPlus, Save } from 'lucide-react';
import { PhoneInput } from '../PhoneInput';

export function UserAddDialog({ open, onOpenChange, api, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', password: '', mobile: '', role: 'member',
    business_name: '', industry_id: '', business_type: '', business_address: '', business_description: '',
    auto_approve_merchant: true,
  });
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setForm({
        name: '', email: '', password: '', mobile: '', role: 'member',
        business_name: '', industry_id: '', business_type: '', business_address: '', business_description: '',
        auto_approve_merchant: true,
      });
      setError('');
    } else {
      api.get('/api/marketplace/industries')
        .then(({ data }) => setIndustries(data.industries || []))
        .catch(() => {});
    }
  }, [open, api]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
    setForm({ ...form, password: pwd });
  };

  const submit = async () => {
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.email.trim() || !form.email.includes('@')) { setError('Valid email is required'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (form.role === 'merchant' && !form.business_name.trim()) { setError('Business name is required for merchant accounts'); return; }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        mobile: form.mobile.trim() || null,
        role: form.role,
      };
      if (form.role === 'merchant') {
        payload.business_name = form.business_name.trim();
        payload.industry_id = form.industry_id || null;
        payload.business_type = form.business_type || null;
        payload.business_address = form.business_address || null;
        payload.business_description = form.business_description || null;
        payload.auto_approve_merchant = form.auto_approve_merchant;
      }
      const { data } = await api.post('/api/admin/users', payload);
      onCreated && onCreated(data, form.password);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create user');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#00E5FF]" /> Add User Manually
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {error && (
            <div className="p-2.5 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-xs" data-testid="add-user-error">
              {error}
            </div>
          )}
          <div className="p-2.5 rounded-md bg-[#00E5FF]/5 border border-[#00E5FF]/20 text-[#00E5FF] text-xs">
            Admin-created accounts skip OCR/ID verification. Share the password with the user securely.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white text-xs">Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#000B18] border-white/20 text-white" data-testid="add-user-name" />
            </div>
            <div>
              <Label className="text-white text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#000B18] border-white/20 text-white" data-testid="add-user-email" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white text-xs">Mobile</Label>
              <PhoneInput
                value={form.mobile}
                onChange={(v) => setForm({ ...form, mobile: v })}
                data-testid="add-user-mobile"
              />
            </div>
            <div>
              <Label className="text-white text-xs">Role</Label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full h-10 bg-[#000B18] border border-white/20 text-white rounded-md px-3 text-sm" data-testid="add-user-role">
                <option value="member">Member</option>
                <option value="merchant">Merchant</option>
                <option value="mro">Merchant Relations Officer</option>
                <option value="cro">Customer Relations Officer</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-white text-xs">Password * (min 6 chars)</Label>
            <div className="flex gap-2">
              <Input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="bg-[#000B18] border-white/20 text-white font-mono" data-testid="add-user-password" />
              <Button type="button" onClick={generatePassword} variant="ghost" className="text-[#00E5FF] whitespace-nowrap" data-testid="add-user-generate-password">
                Generate
              </Button>
            </div>
          </div>

          {form.role === 'merchant' && (
            <div className="space-y-3 p-3 rounded-lg bg-[#00E676]/5 border border-[#00E676]/20">
              <p className="text-[#00E676] text-xs font-medium">Business Information</p>
              <div>
                <Label className="text-white text-xs">Business Name *</Label>
                <Input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} className="bg-[#000B18] border-white/20 text-white" data-testid="add-user-business-name" />
              </div>
              <div>
                <Label className="text-white text-xs">Industry</Label>
                <select value={form.industry_id} onChange={e => setForm({...form, industry_id: e.target.value})} className="w-full h-10 bg-[#000B18] border border-white/20 text-white rounded-md px-3 text-sm" data-testid="add-user-industry">
                  <option value="">Select industry...</option>
                  {industries.map(ind => <option key={ind.id} value={ind.id}>{ind.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-white text-xs">Business Address</Label>
                <Input value={form.business_address} onChange={e => setForm({...form, business_address: e.target.value})} className="bg-[#000B18] border-white/20 text-white" />
              </div>
              <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
                <input type="checkbox" checked={form.auto_approve_merchant} onChange={e => setForm({...form, auto_approve_merchant: e.target.checked})} className="w-4 h-4 accent-[#00E676]" data-testid="add-user-auto-approve" />
                Auto-approve merchant status
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={submit} disabled={loading} className="btn-primary" data-testid="add-user-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Create User</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
